import * as THREE from 'three'
import {
  loadTexture,
  createFilteredArtworkTexture,
  loadFrameTextures,
  createArtworkPlane,
  createMiteredFrame,
  createHangHardware,
  createStretcherBars,
  createSubstrateBackPanel,
  createCanvasWrapMesh,
  ACOUSTIC_PANEL_FABRIC_WEAVE_TILE_INCHES,
  ACOUSTIC_PANEL_FABRIC_WEAVE_NORMAL_SCALE,
  updateCanvasWrapArtwork,
  updateCanvasWrapArtworkUVs,
  updateCanvasWrapGeometry,
  updateCanvasWrapNormalMaps,
  updateSubstrateBackPanelArtwork,
  updateHangHardwareGeometry,
  updateMiteredRailGeometry,
  updateBackLegGeometry,
  updateMatBorderGeometry,
  updateStretcherBarsGeometry,
  createMatBorder,
  createMultiMatBorder,
  createLinenLiner,
  updateLinenLinerGeometry,
  LINEN_LINER_DEPTH,
  FRAME_LIP_OVER_LINER,
  LINEN_LINER_ARTWORK_CLEARANCE,
  computeOuterDimensions,
  resolveArtworkUvTransformTexture,
  applyPerforationAlphaMapScale,
  getCropFromScale,
  inches,
} from '@/utils/frameGeometry.js'
import { resolveArtworkRenderOptions, resolveShowBackPanelWithFrame } from '@/utils/substrates.js'
import { isSharedOverlayMaterial } from './frameGeometry.js'

const DESIGN_STUDIO_WALL_Z = -2
export const DEFAULT_CANVAS_STRETCHER_DEPTH = 1.5
export const FRAMED_CANVAS_STRETCHER_DEPTH = 7 / 16

function createDebugCheckerTexture(aspect = 1) {
  const base = 512
  const safeAspect = aspect > 0 ? aspect : 1
  const texW = safeAspect >= 1 ? base : Math.round(base * safeAspect)
  const texH = safeAspect >= 1 ? Math.round(base / safeAspect) : base
  const canvas = document.createElement('canvas')
  canvas.width = texW
  canvas.height = texH
  const ctx = canvas.getContext('2d')
  if (ctx) {
    const cellPx = Math.max(1, Math.round(base / 16))
    const cellsX = Math.ceil(texW / cellPx)
    const cellsY = Math.ceil(texH / cellPx)
    for (let y = 0; y < cellsY; y += 1) {
      for (let x = 0; x < cellsX; x += 1) {
        ctx.fillStyle = (x + y) % 2 === 0 ? '#ffffff' : '#000000'
        ctx.fillRect(x * cellPx, y * cellPx, cellPx, cellPx)
      }
    }
  }

  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  tex.anisotropy = 16
  tex.needsUpdate = true
  return tex
}

function disposeObject3D(root) {
  if (!root) return
  root.traverse((obj) => {
    if (obj.geometry?.dispose) obj.geometry.dispose()
    const mats = obj.material ? (Array.isArray(obj.material) ? obj.material : [obj.material]) : []
    mats.forEach((m) => {
      if (!m) return
      // Shared overlay materials are owned by the renderer, not by this graph.
      if (isSharedOverlayMaterial(m)) return
      ;['map', 'emissiveMap', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'alphaMap'].forEach((k) => {
        m[k]?.dispose?.()
      })
      m.dispose?.()
    })
  })
}

function parseNumber(value, fallback = 0) {
  const n = parseFloat(value)
  return Number.isFinite(n) ? n : fallback
}

function parseMatWidth(value, hasMat) {
  if (!hasMat) return { top: 0, bottom: 0, left: 0, right: 0 }
  if (value && typeof value === 'object') {
    return {
      top: parseNumber(value.top),
      bottom: parseNumber(value.bottom),
      left: parseNumber(value.left),
      right: parseNumber(value.right),
    }
  }
  const w = parseNumber(value)
  return { top: w, bottom: w, left: w, right: w }
}

function normalizeMatWidth(value = {}) {
  return {
    top: parseNumber(value.top),
    bottom: parseNumber(value.bottom),
    left: parseNumber(value.left),
    right: parseNumber(value.right),
  }
}

function sumMatWidths(widthsArray = []) {
  return widthsArray.reduce((acc, w) => ({
    top: acc.top + parseNumber(w?.top),
    bottom: acc.bottom + parseNumber(w?.bottom),
    left: acc.left + parseNumber(w?.left),
    right: acc.right + parseNumber(w?.right),
  }), { top: 0, bottom: 0, left: 0, right: 0 })
}

function parseMatDepthsArray(matData, matDataArray, matWidth, matWidthsArray) {
  const matArr = matDataArray || (matData ? [matData] : [])
  const hasMatWidth = matWidth.top > 0 || matWidth.bottom > 0 || matWidth.left > 0 || matWidth.right > 0
  const widthsArr = matWidthsArray || (hasMatWidth ? [matWidth] : [])
  const count = Math.max(matArr.length, widthsArr.length)
  return Array.from({ length: count }, (_, idx) => {
    const matDepth = parseFloat(matArr[idx]?.depth)
    return Number.isFinite(matDepth) && matDepth > 0 ? matDepth : 0.0625
  })
}

function resolveTotalMatDepth(props, builtDims, matWidth) {
  if (builtDims?.totalMatDepth != null) return parseNumber(builtDims.totalMatDepth)
  return parseMatDepthsArray(props.matData, props.matDataArray, matWidth, props.matWidthsArray)
    .reduce((sum, depth) => sum + parseNumber(depth), 0)
}

function createMatGroupForProps(props, matWidth, matDepthsArray, artW, artH, isGalleryWrapped = false) {
  if (isGalleryWrapped) return null

  const matArr = props.matDataArray || (props.matData ? [props.matData] : [])
  const widthsArr = props.matWidthsArray || ((matWidth.top || matWidth.bottom || matWidth.left || matWidth.right) ? [matWidth] : [])
  if (!matArr.length || !widthsArr.length) return null

  if (matArr.length > 1) {
    return createMultiMatBorder({
      artWidth: artW,
      artHeight: artH,
      mats: matArr,
      widths: widthsArr,
      depths: matDepthsArray,
    })
  }

  return createMatBorder({
    artWidth: artW,
    artHeight: artH,
    matTop: matWidth.top,
    matBottom: matWidth.bottom,
    matLeft: matWidth.left,
    matRight: matWidth.right,
    color: matArr[0]?.color || '#ffffff',
    depth: matDepthsArray[0] || 0.0625,
    coreColor: matArr[0]?.coreColor || matArr[0]?.color || '#ffffff',
  })
}

function applyTransform(group, spec) {
  const p = spec.position || {}
  const r = spec.rotation || {}
  const s = spec.scale || {}
  group.position.set(parseNumber(p.x), parseNumber(p.y), parseNumber(p.z))
  group.rotation.set(parseNumber(r.x), parseNumber(r.y), parseNumber(r.z))
  group.scale.set(
    parseNumber(s.x, 1),
    parseNumber(s.y, 1),
    parseNumber(s.z, 1)
  )
}

/**
 * Where a piece's front face lands once it is hung: the wall plane, plus how far
 * the piece stands off it.
 *
 * Exported so a camera fit can be computed before the geometry exists. The
 * builder places the group by this same rule in applyBackToWallAlignment, and
 * reports the result as builtDims.frontZ — so a fit taken from props before a
 * rebuild agrees with the one taken from the built scene after it, and the
 * camera has nothing left to correct.
 */
export function resolveDesignStudioItemFrontZ(input = {}, baseZ = 0) {
  const metrics = resolveDesignStudioItemSceneMetrics(input)
  const backDepth = Math.max(0, parseNumber(metrics.backDepth))
  return baseZ + DESIGN_STUDIO_WALL_Z + inches(backDepth) + parseNumber(metrics.localFrontZ)
}

function applyBackToWallAlignment(group, backDepthInches) {
  const backDepth = Math.max(0, parseNumber(backDepthInches))
  group.position.z += DESIGN_STUDIO_WALL_Z + inches(backDepth)
}

function stableSignature(value) {
  if (value == null) return ''
  if (Array.isArray(value)) {
    return `[${value.map(stableSignature).join(',')}]`
  }
  if (typeof value === 'object') {
    return `{${Object.keys(value).sort().map(key => `${key}:${stableSignature(value[key])}`).join(',')}}`
  }
  return JSON.stringify(value)
}

function resolveMatWidthForMetrics(props, builtDims, overrides, isGalleryWrapped) {
  if (isGalleryWrapped) return { top: 0, bottom: 0, left: 0, right: 0 }
  if (Array.isArray(overrides.matWidthsArray) && overrides.matWidthsArray.length) {
    return sumMatWidths(overrides.matWidthsArray)
  }
  if (overrides.matWidth != null) return normalizeMatWidth(overrides.matWidth)
  if (Array.isArray(props.matWidthsArray) && props.matWidthsArray.length) {
    return sumMatWidths(props.matWidthsArray)
  }
  const builtMatWidth = builtDims?.matWidth || builtDims?.mw
  if (builtMatWidth) return normalizeMatWidth(builtMatWidth)
  const hasMat = Boolean(props.matData || (props.matDataArray && props.matDataArray.length))
  return parseMatWidth(props.matWidth, hasMat)
}

/**
 * Linen liner width per side, in inches.
 *
 * Only applies to canvas mounted in a frame. The liner is the fabric surround
 * inside a frame's rabbet, so it is meaningless gallery-wrapped (nothing to
 * line) and on a float frame (the gap is the point). A stale option left on the
 * item after a mount change is ignored here rather than cleared, the same way
 * `showFrame` ignores a stale frame once the mount is gallery wrapped.
 *
 * Legacy stores the width as the option's `num_value` — the active choices are
 * 1" and 2" — and adds it on every side, so it is a width, not a flag.
 */
function resolveLinenLinerWidth(props, builtDims, overrides, isCanvas, canvasMount) {
  if (!isCanvas) return 0
  if (!/^frame$/i.test(String(canvasMount || '').trim())) return 0
  const raw = overrides.linenLiner ?? props.linenLiner ?? builtDims?.linenLinerWidth
  return Math.max(0, parseNumber(raw))
}

export function resolveDesignStudioItemSceneMetrics(input = {}) {
  const props = input.rendererProps || input.props || {}
  const builtDims = input.builtDims || null
  const overrides = input.overrides || {}

  const artW = parseNumber(overrides.artWidth ?? overrides.width ?? props.artWidth ?? builtDims?.artW, 16)
  const artH = parseNumber(overrides.artHeight ?? overrides.height ?? props.artHeight ?? builtDims?.artH, 20)
  const substrate = String(overrides.substrate ?? props.substrate ?? builtDims?.substrate ?? '')
  const isCanvas = substrate.toLowerCase() === 'canvas'
  const isAcousticPanel = /acoustic/i.test(substrate)
  const canvasMount = String(overrides.canvasMount ?? props.canvasMount ?? builtDims?.canvasMount ?? '').trim()
  const isGalleryWrapped = isCanvas && /gallery\s*wrapped/i.test(canvasMount)
  const isFloatFrame = builtDims?.isFloatFrame != null
    ? Boolean(builtDims.isFloatFrame)
    : (isCanvas && /float\s*frame/i.test(canvasMount))
  const matWidth = resolveMatWidthForMetrics(props, builtDims, overrides, isGalleryWrapped)
  const totalMatDepth = resolveTotalMatDepth(props, builtDims, matWidth)
  const linenLinerWidth = resolveLinenLinerWidth(props, builtDims, overrides, isCanvas, canvasMount)


  const effectiveFrameData = isFloatFrame && props.floatFrameData ? props.floatFrameData : props.frameData
  const frameWidthFromProps = isFloatFrame && props.floatFrameData
    ? parseNumber(props.floatFrameData.width)
    : parseNumber(effectiveFrameData?.width)
  const frameWidth = parseNumber(overrides.frameWidth ?? builtDims?.frameWidth, frameWidthFromProps)
  const showFrame = !isGalleryWrapped && frameWidth > 0
  const frameRabbet = showFrame && !isFloatFrame
    ? Math.max(0, parseNumber(
      overrides.frameRabbet ?? builtDims?.frameRabbet,
      effectiveFrameData?.rabbet != null ? parseNumber(effectiveFrameData.rabbet) : 0
    ))
    : 0
  const floatFrameGap = isFloatFrame
    ? parseNumber(overrides.floatFrameGap ?? builtDims?.floatFrameGap ?? props.floatFrameData?.gap, 0.25)
    : 0
  const selectedStretcherDepth = parseNumber(overrides.stretcherDepth ?? props.stretcherDepth ?? builtDims?.stretcherDepth)
  const usesThinCanvasStretcher = !isFloatFrame && !isGalleryWrapped
    && (showFrame || /^frame$/i.test(canvasMount))
  const stretcherDepth = isCanvas
    ? (selectedStretcherDepth > 0
      ? selectedStretcherDepth
      : (usesThinCanvasStretcher ? FRAMED_CANVAS_STRETCHER_DEPTH : DEFAULT_CANVAS_STRETCHER_DEPTH))
    : selectedStretcherDepth
  const acousticPanelDepth = (isAcousticPanel && (props.substrateBackData?.depth ?? builtDims?.substrateBackDepth) > 0)
    ? parseNumber(props.substrateBackData?.depth ?? builtDims?.substrateBackDepth)
    : 0
  const fabricWrapDepthInches = isCanvas
    ? stretcherDepth
    : (isAcousticPanel && acousticPanelDepth > 0 ? acousticPanelDepth : 0)
  const wrapDepthForFrame = isCanvas
    ? stretcherDepth
    : (isAcousticPanel && fabricWrapDepthInches > 0 ? fabricWrapDepthInches : 0)
  const linenLinerDepth = linenLinerWidth > 0 ? LINEN_LINER_DEPTH : 0
  const frameDepth = showFrame
    ? Math.max(
      wrapDepthForFrame > 0
        ? wrapDepthForFrame
        : (frameRabbet > 0
          ? frameRabbet + (frameRabbet > 2 ? 0.5 : 0.25)
          : Math.max(frameWidth * 0.75, 0.5)),
      // A liner seats in the rabbet, so a lined frame needs room for it and
      // enough lip left over to stay proud of it. Without this the frame's
      // derived lip is a flat 1/4" whatever the moulding, the liner fills it
      // exactly, and the two faces land in one plane. Unlined frames are
      // unaffected — their base depth already exceeds this floor.
      linenLinerDepth > 0 ? frameRabbet + linenLinerDepth + FRAME_LIP_OVER_LINER : 0
    )
    : 0
  const frameColor = effectiveFrameData?.color || effectiveFrameData?.frameColor || builtDims?.frameColor || null
  const frameLipDepth = Math.max(0, frameDepth - frameRabbet)
  // A framed canvas normally sits flush with the frame face, but a liner has to
  // sit down inside the rabbet with the frame's lip standing proud of it — the
  // cross-section runs moulding highest, liner stepped down, canvas lowest. So a
  // lined canvas recesses into the rabbet the way a print already does.
  const recessesIntoRabbet = frameRabbet > 0 && !isAcousticPanel && !isFloatFrame
    && (!isCanvas || linenLinerWidth > 0)
  const interiorFrontZ = recessesIntoRabbet ? -frameLipDepth : 0
  const substrateBorder = parseNumber(overrides.substrateBorder ?? props.substrateBackData?.border ?? builtDims?.substrateBorder)
  const substrateBackDepth = parseNumber(props.substrateBackData?.depth ?? builtDims?.substrateBackDepth)
  const backDepth = (() => {
    if (isCanvas) return Math.max(frameDepth, stretcherDepth - interiorFrontZ)
    if (isAcousticPanel && fabricWrapDepthInches > 0) return fabricWrapDepthInches
    return frameDepth > 0 ? frameDepth : (substrateBackDepth > 0 ? substrateBackDepth : 0.1)
  })()

  const matInnerW = artW + matWidth.left + matWidth.right
  const matInnerH = artH + matWidth.top + matWidth.bottom
  // A liner and a frame are the same construction: a face of the stated width
  // with a lip lapping over whatever sits inside, so each hides its lip from the
  // contents and reaches (width − lip) beyond them. The liner wraps the mats,
  // then the frame wraps the liner — the stack legacy builds too, applying the
  // liner moulding to the comp first.
  //
  // Both lips are 0 today, which is legacy's model: it reads no rabbet at all
  // (`rabbet` appears nowhere in project/models.py), and no catalogue frame
  // carries a `rabbet_width`. Finished size is API-authoritative and priced on
  // that basis, so lapping the trade-standard 1/4" here would disagree with our
  // own price — raise these together with the server-side change.
  const linenLinerRabbet = 0
  const frameRabbetWidth = frameRabbet > 0 && !isFloatFrame
    ? Math.max(0, parseNumber(effectiveFrameData?.rabbet_width))
    : 0
  const linenLinerReach = Math.max(0, linenLinerWidth - linenLinerRabbet)
  const linerInnerW = matInnerW + (2 * linenLinerReach)
  const linerInnerH = matInnerH + (2 * linenLinerReach)
  const frameOpeningW = Math.max(0.01, linerInnerW - (2 * frameRabbetWidth))
  const frameOpeningH = Math.max(0.01, linerInnerH - (2 * frameRabbetWidth))
  const frameInnerW = (isFloatFrame ? linerInnerW + (2 * floatFrameGap) : frameOpeningW)
  const frameInnerH = (isFloatFrame ? linerInnerH + (2 * floatFrameGap) : frameOpeningH)
  const substrateBackW = matInnerW + (2 * substrateBorder)
  const substrateBackH = matInnerH + (2 * substrateBorder)
  const matOffsetX = (matWidth.right - matWidth.left) / 2
  const matOffsetY = (matWidth.top - matWidth.bottom) / 2
  const frameZ = (isCanvas || isAcousticPanel || showFrame) ? -inches(frameDepth) : 0
  const localFrontZ = 0
  const hangHwUsesArtSize = builtDims?.hangHwUsesArtSize != null
    ? Boolean(builtDims.hangHwUsesArtSize)
    : (isCanvas || (isAcousticPanel && fabricWrapDepthInches > 0))
  const hangInnerW = hangHwUsesArtSize ? artW : linerInnerW
  const hangInnerH = hangHwUsesArtSize ? artH : linerInnerH
  const hangData = overrides.hangData ?? props.hangData ?? builtDims?.hangData ?? null
  const artworkOpts = input.artworkOpts || {}
  const shouldIntegrateArtworkIntoFabricWrap = builtDims?.hasFabricWrap != null
    ? Boolean(builtDims.hasFabricWrap)
    : Boolean(
      fabricWrapDepthInches > 0
      && !artworkOpts.transparent
      && !artworkOpts.perforated
      && artworkOpts.side !== THREE.DoubleSide
    )
  const shouldIntegrateArtworkIntoSubstrate = builtDims?.hasIntegratedSubstrateArtwork != null
    ? Boolean(builtDims.hasIntegratedSubstrateArtwork)
    : Boolean(
      props.substrateBackData?.depth > 0
      && !isCanvas
      && !isAcousticPanel
      && artworkOpts.side !== THREE.DoubleSide
      && !artworkOpts.transparent
      && !artworkOpts.perforated
    )
  const outer = computeOuterDimensions({
    artWidth: isFloatFrame ? artW + (2 * floatFrameGap) : artW,
    artHeight: isFloatFrame ? artH + (2 * floatFrameGap) : artH,
    matTop: matWidth.top,
    matBottom: matWidth.bottom,
    matLeft: matWidth.left,
    matRight: matWidth.right,
    frameWidth: showFrame ? Math.max(0, frameWidth - frameRabbetWidth) : 0,
    rabbet: frameRabbet,
    substrateBorder,
    linerWidth: linenLinerReach,
  })
  const outerWidth = parseNumber(outer.outerWidth, artW)
  const outerHeight = parseNumber(outer.outerHeight, artH)
  const hasFrameMountedHardware = showFrame && (substrate.toLowerCase() === 'print' || isCanvas)
  const hangHardwareW = hasFrameMountedHardware ? outerWidth : hangInnerW
  const hangHardwareH = hasFrameMountedHardware ? outerHeight : hangInnerH
  const hangWireAnchorW = hasFrameMountedHardware ? hangInnerW : null
  const hangSecurityVerticalInset = hasFrameMountedHardware ? 0.25 : null

  return {
    artW,
    artH,
    planeW: artW,
    planeH: artH,
    substrate,
    isCanvas,
    isAcousticPanel,
    isGalleryWrapped,
    isFloatFrame,
    canvasMount,
    matWidth,
    totalMatDepth,
    matInnerW,
    matInnerH,
    linenLinerWidth,
    linenLinerDepth,
    linenLinerRabbet,
    linenLinerReach,
    linerInnerW,
    linerInnerH,
    matOffsetX,
    matOffsetY,
    frameData: effectiveFrameData,
    frameWidth: showFrame ? frameWidth : 0,
    frameRabbet,
    frameRabbetWidth,
    frameColor,
    frameDepth,
    interiorFrontZ,
    frameInnerW,
    frameInnerH,
    frameZ,
    showFrame,
    floatFrameGap,
    stretcherDepth,
    acousticPanelDepth,
    fabricWrapDepthInches,
    shouldIntegrateArtworkIntoFabricWrap,
    shouldIntegrateArtworkIntoSubstrate,
    substrateBorder,
    substrateBackDepth,
    substrateBackW,
    substrateBackH,
    backDepth,
    localFrontZ,
    hangData,
    hangHwUsesArtSize,
    hangInnerW,
    hangInnerH,
    hangHardwareW,
    hangHardwareH,
    hangWireAnchorW,
    hangSecurityVerticalInset,
    outerWidth,
    outerHeight,
  }
}

function createBuiltDimsFromMetrics(metrics, group, props = {}) {
  return {
    artW: metrics.artW,
    artH: metrics.artH,
    outerWidth: metrics.outerWidth,
    outerHeight: metrics.outerHeight,
    matOffsetX: metrics.matOffsetX,
    matOffsetY: metrics.matOffsetY,
    backDepth: metrics.backDepth,
    localFrontZ: metrics.localFrontZ,
    frontZ: (group?.position?.z || 0) + metrics.localFrontZ,
    matWidth: { ...metrics.matWidth },
    totalMatDepth: metrics.totalMatDepth,
    linenLinerWidth: metrics.linenLinerWidth,
    linenLinerDepth: metrics.linenLinerDepth,
    linenLinerRabbet: metrics.linenLinerRabbet,
    frameWidth: metrics.frameWidth,
    frameRabbet: metrics.frameRabbet,
    frameRabbetWidth: metrics.frameRabbetWidth,
    frameColor: metrics.frameColor,
    frameDepth: metrics.frameDepth,
    interiorFrontZ: metrics.interiorFrontZ,
    canvasMount: metrics.canvasMount,
    isFloatFrame: metrics.isFloatFrame,
    floatFrameGap: metrics.floatFrameGap,
    substrateBorder: metrics.substrateBorder,
    substrateBackDepth: metrics.substrateBackDepth,
    substrate: metrics.substrate,
    stretcherDepth: metrics.stretcherDepth,
    hangData: props.hangData || metrics.hangData || null,
    hangHwUsesArtSize: metrics.hangHwUsesArtSize,
    hangHardwareW: metrics.hangHardwareW,
    hangHardwareH: metrics.hangHardwareH,
    hangWireAnchorW: metrics.hangWireAnchorW,
    hangSecurityVerticalInset: metrics.hangSecurityVerticalInset,
    hasFabricWrap: metrics.shouldIntegrateArtworkIntoFabricWrap,
    hasIntegratedSubstrateArtwork: metrics.shouldIntegrateArtworkIntoSubstrate,
  }
}

export async function buildDesignStudioItemObject(renderSpec) {
  const props = renderSpec?.rendererProps || {}
  const selected = Boolean(renderSpec?.selected || renderSpec?.active)
  const group = new THREE.Group()
  group.userData = {
    type: 'design-studio-item',
    key: renderSpec?.key,
    itemId: renderSpec?.itemId,
    selected,
    active: selected,
    affordances: renderSpec?.affordances || {},
    propsSignature: stableSignature(props),
  }
  applyTransform(group, renderSpec || {})

  const artworkRenderOpts = resolveArtworkRenderOptions(props.substrate, props.substrateBackData)
  const artworkOpts = artworkRenderOpts ? {
    side: artworkRenderOpts.side === 'DoubleSide' ? THREE.DoubleSide : THREE.FrontSide,
    opacity: artworkRenderOpts.opacity ?? 1,
    transparent: artworkRenderOpts.transparent ?? false,
    perforated: artworkRenderOpts.perforated ?? false,
  } : {}
  const metrics = resolveDesignStudioItemSceneMetrics({ rendererProps: props, artworkOpts })
  const {
    artW,
    artH,
    substrate,
    isCanvas,
    isAcousticPanel,
    isFloatFrame,
    matWidth,
    effectiveFrameData,
  } = {
    ...metrics,
    effectiveFrameData: metrics.frameData,
  }
  const matDepthsArray = parseMatDepthsArray(props.matData, props.matDataArray, matWidth, props.matWidthsArray)

  const loadedArtworkTex = props.artworkImageUrl ? await loadTexture(props.artworkImageUrl) : null
  const loadedImage = loadedArtworkTex?.image
  const loadedAspect = loadedImage?.width > 0 && loadedImage?.height > 0
    ? loadedImage.width / loadedImage.height
    : artW / artH
  const originalArtworkTex = props.debugChecker
    ? createDebugCheckerTexture(loadedAspect)
    : loadedArtworkTex
  const artworkTex = originalArtworkTex ? createFilteredArtworkTexture(originalArtworkTex, props.artworkFilter) : null
  const frameKey = effectiveFrameData?.top_image || ''
  const frameTextures = metrics.showFrame && frameKey ? await loadFrameTextures(effectiveFrameData) : { front: null, side: null, top: null, sampledColor: null }
  const imgAspect = (() => {
    const img = artworkTex?.image
    const w = img?.width || img?.naturalWidth
    const h = img?.height || img?.naturalHeight
    return w > 0 && h > 0 ? w / h : artW / artH
  })()
  const cropDims = getCropFromScale(
    artW,
    artH,
    parseNumber(props.artworkScaleX, 1),
    parseNumber(props.artworkOffsetLeft),
    parseNumber(props.artworkOffsetTop),
    imgAspect,
    3,
    parseNumber(props.artworkScaleY ?? props.artworkScaleX, 1),
    parseNumber(props.artworkRotation)
  )
  const cropOpts = {
    ...artworkOpts,
    fullWidth: cropDims.fullWidth,
    fullHeight: cropDims.fullHeight,
    cropLeft: cropDims.cropLeft,
    cropTop: cropDims.cropTop,
    scaleX: parseNumber(props.artworkScaleX, 1),
    scaleY: parseNumber(props.artworkScaleY ?? props.artworkScaleX, 1),
    rotation: parseNumber(props.artworkRotation),
  }

  let artworkMesh = null
  let canvasWrapMesh = null
  if (metrics.shouldIntegrateArtworkIntoFabricWrap) {
    canvasWrapMesh = createCanvasWrapMesh(artW, artH, metrics.fabricWrapDepthInches, artworkTex, {
      ...cropOpts,
      suppressFabricFrontBevel: Boolean(isAcousticPanel),
      blackWrapSideEdges: Boolean(isAcousticPanel),
      plainAcousticBack: Boolean(isAcousticPanel && !props.acousticTwoSidedChangeable),
      acousticDoubleSidedArtwork: Boolean(isAcousticPanel && props.acousticTwoSidedChangeable),
      ...(isAcousticPanel
        ? {
          fabricWeaveTileInches: ACOUSTIC_PANEL_FABRIC_WEAVE_TILE_INCHES,
          fabricWeaveNormalScale: ACOUSTIC_PANEL_FABRIC_WEAVE_NORMAL_SCALE,
        }
        : {}),
    })
  } else if (!metrics.shouldIntegrateArtworkIntoSubstrate) {
    artworkMesh = createArtworkPlane(artW, artH, artworkTex, cropOpts)
    artworkMesh.position.z = inches(metrics.interiorFrontZ)
    artworkMesh.renderOrder = 1
  }
  if (canvasWrapMesh) {
    // Seat the stretched canvas where every other interior layer seats. Without
    // this the wrap sat at the frame face while the liner recessed behind it,
    // so the canvas stood proud of the lip meant to cover its edge. Its depth
    // runs backwards from here, so a canvas deeper than the recess leaves out
    // the back — which is why liner canvases use thin (7/16") stretcher bars.
    canvasWrapMesh.position.z = inches(metrics.interiorFrontZ)
  }

  const matGroup = createMatGroupForProps(props, matWidth, matDepthsArray, artW, artH, metrics.isGalleryWrapped)
  if (matGroup) {
    matGroup.position.z = metrics.frameRabbet > 0
      ? inches(metrics.interiorFrontZ - metrics.totalMatDepth)
      : 0
  }

  // Linen liner wraps whatever the mats leave exposed, and the frame wraps it.
  const linenLinerGroup = createLinenLiner({
    artWidth: metrics.matInnerW - (2 * metrics.linenLinerRabbet),
    artHeight: metrics.matInnerH - (2 * metrics.linenLinerRabbet),
    linerWidth: metrics.linenLinerWidth,
    depth: metrics.linenLinerDepth,
  })
  if (linenLinerGroup) {
    // The band extrudes forward from its plane, so seating it just behind the
    // artwork puts the lip in front of the canvas edge and steps the liner up
    // off it — the frame then steps up around the liner in turn.
    linenLinerGroup.position.set(
      inches(metrics.matOffsetX),
      inches(metrics.matOffsetY),
      inches(metrics.interiorFrontZ - LINEN_LINER_ARTWORK_CLEARANCE)
    )
  }

  let frameGroup = null
  if (metrics.showFrame) {
    frameGroup = createMiteredFrame({
      artWidth: metrics.frameInnerW,
      artHeight: metrics.frameInnerH,
      frameWidth: metrics.frameWidth,
      rabbet: metrics.frameRabbet,
      depth: metrics.frameDepth,
      textures: frameTextures,
      isFloatFrame,
      frameColor: metrics.frameColor ?? frameTextures.sampledColor,
    })
    frameGroup.position.set(
      metrics.matOffsetX,
      metrics.matOffsetY,
      metrics.frameZ
    )
  }

  let substrateBackMesh = null
  if (
    !isAcousticPanel
    && props.substrateBackData
    && props.substrateBackData.depth > 0
    && (!metrics.showFrame || resolveShowBackPanelWithFrame(substrate, props.substrateBackData))
  ) {
    substrateBackMesh = createSubstrateBackPanel(metrics.substrateBackW, metrics.substrateBackH, {
      ...props.substrateBackData,
      artworkTexture: metrics.shouldIntegrateArtworkIntoSubstrate ? artworkTex : null,
      artworkWidth: metrics.shouldIntegrateArtworkIntoSubstrate ? artW : 0,
      artworkHeight: metrics.shouldIntegrateArtworkIntoSubstrate ? artH : 0,
      fullWidth: metrics.shouldIntegrateArtworkIntoSubstrate ? cropDims.fullWidth : null,
      fullHeight: metrics.shouldIntegrateArtworkIntoSubstrate ? cropDims.fullHeight : null,
      cropLeft: metrics.shouldIntegrateArtworkIntoSubstrate ? cropDims.cropLeft : null,
      cropTop: metrics.shouldIntegrateArtworkIntoSubstrate ? cropDims.cropTop : null,
      scaleX: cropOpts.scaleX,
      scaleY: cropOpts.scaleY,
      rotation: cropOpts.rotation,
    })
    substrateBackMesh.position.set(inches(metrics.matOffsetX), inches(metrics.matOffsetY), 0)
  }

  const stretcherGroup = isCanvas
    ? createStretcherBars(artW, artH, metrics.stretcherDepth)
    : null
  if (stretcherGroup) {
    // The support and its fabric shell are one physical assembly. A linen
    // liner seats that assembly farther into the frame rabbet, so the bars
    // must follow the canvas instead of remaining at the un-recessed origin.
    stretcherGroup.position.z = inches(metrics.interiorFrontZ)
  }

  applyBackToWallAlignment(group, metrics.backDepth)

  let hangHardwareGroup = null
  if (props.hangData) {
    const hangName = props.hangData.name || props.hangData.value || ''
    hangHardwareGroup = createHangHardware({
      innerWidth: metrics.hangInnerW,
      innerHeight: metrics.hangInnerH,
      hangType: hangName,
      substrate,
      backDepth: metrics.backDepth,
      hardwareWidth: metrics.hangHardwareW,
      hardwareHeight: metrics.hangHardwareH,
      wireAnchorWidth: metrics.hangWireAnchorW,
      securityVerticalInset: metrics.hangSecurityVerticalInset,
    })
    hangHardwareGroup.position.set(inches(metrics.matOffsetX), inches(metrics.matOffsetY), 0)
  }

  [artworkMesh, canvasWrapMesh, matGroup, linenLinerGroup, stretcherGroup, substrateBackMesh, frameGroup, hangHardwareGroup]
    .filter(Boolean)
    .forEach(obj => group.add(obj))

  group.traverse((obj) => {
    if (obj !== group) {
      obj.userData = {
        ...obj.userData,
        renderItemKey: renderSpec?.key,
        renderItemId: renderSpec?.itemId,
        selectedRenderItem: selected,
        itemArtWidth: artW,
        itemArtHeight: artH,
        itemOuterWidth: metrics.outerWidth,
        itemOuterHeight: metrics.outerHeight,
      }
    }
    if (obj.isMesh) {
      // Stretcher bars sit immediately behind a very thin canvas plane.
      // Letting them cast into the front surface produces false horizontal and
      // vertical lines from shadow-map bias, most visibly on gallery wraps.
      obj.castShadow = obj.userData?.type !== 'stretcher-rail'
      obj.receiveShadow = true
    }
  })

  return {
    key: renderSpec?.key,
    itemId: renderSpec?.itemId,
    group,
    refs: { artworkMesh, canvasWrapMesh, matGroup, linenLinerGroup, frameGroup, substrateBackMesh, stretcherGroup, hangHardwareGroup },
    builtDims: createBuiltDimsFromMetrics(metrics, group, props),
    dispose() {
      disposeObject3D(group)
    },
  }
}

function artworkUvTransformOptions(artW, artH, crop, transform = {}) {
  return {
    planeWidth: artW,
    planeHeight: artH,
    fullWidth: crop.fullWidth,
    fullHeight: crop.fullHeight,
    cropLeft: crop.cropLeft,
    cropTop: crop.cropTop,
    scaleX: transform.scaleX ?? transform.scale,
    scaleY: transform.scaleY ?? transform.scaleX ?? transform.scale,
    rotation: transform.rotation,
  }
}

function updateArtworkMeshTexture(mesh, artworkTexture, artW, artH, crop, transform = {}) {
  const prev = mesh?.material?.map
  const next = resolveArtworkUvTransformTexture(
    prev,
    artworkTexture || prev,
    artworkUvTransformOptions(artW, artH, crop, transform)
  )
  if (!next) return
  if (next !== prev) {
    mesh.material.map = next
    prev?.dispose?.()
  }
  mesh.material.needsUpdate = true
}

function updateArtworkGeometry(instance, artW, artH, crop, artworkTexture, sourceTransform = null) {
  if (instance.refs?.artworkMesh) {
    instance.refs.artworkMesh.geometry?.dispose?.()
    instance.refs.artworkMesh.geometry = new THREE.PlaneGeometry(inches(artW), inches(artH))
    instance.refs.artworkMesh.scale.set(1, 1, 1)
    updateArtworkMeshTexture(instance.refs.artworkMesh, artworkTexture, artW, artH, crop, sourceTransform)
    applyPerforationAlphaMapScale(instance.refs.artworkMesh.material, artW, artH)
    return
  }

  if (instance.refs?.canvasWrapMesh) {
    updateCanvasWrapGeometry(instance.refs.canvasWrapMesh, artW, artH)
    updateCanvasWrapArtworkUVs(instance.refs.canvasWrapMesh, artW, artH)
    updateCanvasWrapNormalMaps(instance.refs.canvasWrapMesh, artW, artH)
    updateCanvasWrapArtwork(instance.refs.canvasWrapMesh, {
      artworkTexture: artworkTexture || instance.refs.canvasWrapMesh.userData?.artworkTexture,
      artworkWidth: artW,
      artworkHeight: artH,
      fullWidth: crop.fullWidth,
      fullHeight: crop.fullHeight,
      cropLeft: crop.cropLeft,
      cropTop: crop.cropTop,
      scaleX: sourceTransform?.scaleX ?? sourceTransform?.scale,
      scaleY: sourceTransform?.scaleY ?? sourceTransform?.scaleX ?? sourceTransform?.scale,
      rotation: sourceTransform?.rotation,
    })
  }
}

function updateMatGeometry(instance, artW, artH, matWidthsArray = null) {
  if (!instance.refs?.matGroup) return
  if (Array.isArray(matWidthsArray)) {
    instance.refs.matGroup.children.forEach(mesh => {
      if (!mesh?.isMesh || mesh.userData?.type !== 'mat-piece') return
      const idx = (mesh.userData.matNum || 1) - 1
      const width = matWidthsArray[idx]
      if (!width) return
      mesh.userData.widthsInches = normalizeMatWidth(width)
    })
  }
  updateMatBorderGeometry(instance.refs.matGroup, artW, artH)
}

function updateInteriorStackPositions(instance, metrics) {
  if (instance.refs?.artworkMesh) {
    instance.refs.artworkMesh.position.z = inches(metrics.interiorFrontZ)
  }
  if (instance.refs?.canvasWrapMesh) {
    instance.refs.canvasWrapMesh.position.z = inches(metrics.interiorFrontZ)
  }
  if (instance.refs?.matGroup) {
    instance.refs.matGroup.position.z = metrics.frameRabbet > 0
      ? inches(metrics.interiorFrontZ - metrics.totalMatDepth)
      : 0
  }
  if (instance.refs?.linenLinerGroup) {
    instance.refs.linenLinerGroup.position.set(
      inches(metrics.matOffsetX),
      inches(metrics.matOffsetY),
      inches(metrics.interiorFrontZ - LINEN_LINER_ARTWORK_CLEARANCE)
    )
  }
}

function updateFrameGeometry(instance, metrics) {
  const frameGroup = instance.refs?.frameGroup
  if (!frameGroup) return

  frameGroup.position.x = inches(metrics.matOffsetX)
  frameGroup.position.y = inches(metrics.matOffsetY)
  frameGroup.position.z = inches(metrics.frameZ)
  frameGroup.traverse((obj) => {
    const pos = obj.userData?.position
    if (obj.userData?.type === 'frame-rail') {
      updateMiteredRailGeometry(
        obj,
        (pos === 'top' || pos === 'bottom') ? metrics.frameInnerW : metrics.frameInnerH,
        metrics.frameWidth,
        pos,
        metrics.frameDepth,
        metrics.isFloatFrame
      )
      if (pos === 'bottom') obj.scale.y = -1
      if (pos === 'top' || pos === 'bottom') {
        obj.position.x = 0
        obj.position.y = inches((pos === 'top' ? 1 : -1) * metrics.frameInnerH / 2)
      } else if (pos === 'left' || pos === 'right') {
        obj.position.x = inches((pos === 'right' ? 1 : -1) * metrics.frameInnerW / 2)
        obj.position.y = 0
      }
    } else if (obj.userData?.type === 'frame-back-leg') {
      updateBackLegGeometry(obj, metrics.frameInnerW, metrics.frameInnerH, metrics.frameWidth, metrics.frameDepth)
    }
  })
}

function updateSubstrateBackArtwork(substrateBackMesh, metrics, context = {}) {
  if (!substrateBackMesh?.userData?.integratedArtwork || !context.sourceCrop) return
  updateSubstrateBackPanelArtwork(substrateBackMesh, {
    color: context.substrateBackColor,
    panelWidth: metrics.substrateBackW,
    panelHeight: metrics.substrateBackH,
    artworkWidth: metrics.artW,
    artworkHeight: metrics.artH,
    artworkTexture: context.artworkTexture || substrateBackMesh.userData?.artworkTexture,
    fullWidth: context.sourceCrop.fullWidth,
    fullHeight: context.sourceCrop.fullHeight,
    cropLeft: context.sourceCrop.cropLeft,
    cropTop: context.sourceCrop.cropTop,
    scaleX: context.sourceTransform?.scaleX ?? context.sourceTransform?.scale,
    scaleY: context.sourceTransform?.scaleY ?? context.sourceTransform?.scaleX ?? context.sourceTransform?.scale,
    rotation: context.sourceTransform?.rotation,
  })
}

function updateSubstrateBackGeometry(instance, metrics, context = {}) {
  const substrateBackMesh = instance.refs?.substrateBackMesh
  if (!substrateBackMesh) return

  substrateBackMesh.traverse((obj) => {
    if (!obj.isMesh) return
    if (obj.geometry?.parameters?.width && obj.geometry?.parameters?.height && obj.geometry?.parameters?.depth) {
      const depth = obj.geometry.parameters.depth / inches(1)
      obj.geometry.dispose()
      obj.geometry = new THREE.BoxGeometry(inches(metrics.substrateBackW), inches(metrics.substrateBackH), inches(depth))
      obj.position.z = -inches(depth) / 2
    } else if (obj.userData?.type === 'panel-bevel-rim') {
      const baseW = parseNumber(obj.userData.panelWidth, metrics.substrateBackW)
      const baseH = parseNumber(obj.userData.panelHeight, metrics.substrateBackH)
      obj.scale.set(
        baseW > 0 ? metrics.substrateBackW / baseW : 1,
        baseH > 0 ? metrics.substrateBackH / baseH : 1,
        1
      )
    }
  })

  updateSubstrateBackArtwork(substrateBackMesh, metrics, context)
}

function updateHangHardware(instance, metrics) {
  const hangHardwareGroup = instance.refs?.hangHardwareGroup
  const builtDims = instance.builtDims || {}
  if (!hangHardwareGroup) return

  const hangName = builtDims.hangData?.name || builtDims.hangData?.value || ''
  updateHangHardwareGeometry(hangHardwareGroup, {
    innerWidth: metrics.hangInnerW,
    innerHeight: metrics.hangInnerH,
    hangType: hangName,
    substrate: metrics.substrate,
    backDepth: metrics.backDepth,
    hardwareWidth: metrics.hangHardwareW,
    hardwareHeight: metrics.hangHardwareH,
    wireAnchorWidth: metrics.hangWireAnchorW,
    securityVerticalInset: metrics.hangSecurityVerticalInset,
  })
}

function updateBuiltDimensions(instance, metrics) {
  if (!instance.builtDims) return
  const next = createBuiltDimsFromMetrics(metrics, instance.group, instance.builtDims)
  Object.keys(instance.builtDims).forEach(key => delete instance.builtDims[key])
  Object.assign(instance.builtDims, next)
}

function updateDependentShellGeometry(instance, metrics, {
  sourceCrop = null,
  sourceTransform = null,
  artworkTexture = null,
  substrateBackColor = null,
  matWidthsArray = null,
} = {}) {
  if (!(metrics?.artW > 0) || !(metrics?.artH > 0)) return
  updateMatGeometry(instance, metrics.artW, metrics.artH, matWidthsArray)
  if (instance.refs?.linenLinerGroup) {
    updateLinenLinerGeometry(
      instance.refs.linenLinerGroup,
      metrics.matInnerW - (2 * metrics.linenLinerRabbet),
      metrics.matInnerH - (2 * metrics.linenLinerRabbet)
    )
  }
  updateInteriorStackPositions(instance, metrics)
  updateFrameGeometry(instance, metrics)
  updateSubstrateBackGeometry(instance, metrics, { sourceCrop, sourceTransform, artworkTexture, substrateBackColor })
  if (instance.refs?.stretcherGroup) {
    updateStretcherBarsGeometry(instance.refs.stretcherGroup, metrics.artW, metrics.artH, metrics.stretcherDepth)
    instance.refs.stretcherGroup.position.z = inches(metrics.interiorFrontZ)
  }
  updateHangHardware(instance, metrics)
  updateBuiltDimensions(instance, metrics)
}

export function applyDesignStudioItemResizeVisual(instance, {
  width,
  height,
  sourceTransform,
  imageAspect,
  artworkTexture = null,
  substrateBackColor = null,
  baseDims = null,
  baseScale = null,
} = {}) {
  if (!instance?.refs || !instance.group) return
  const artW = parseNumber(width)
  const artH = parseNumber(height)
  const dims = baseDims || instance.builtDims || {}
  if (!(artW > 0) || !(artH > 0)) return
  if (baseScale) instance.group.scale.copy(baseScale)
  const metrics = resolveDesignStudioItemSceneMetrics({
    builtDims: dims,
    overrides: { artWidth: artW, artHeight: artH },
  })

  const transform = sourceTransform || { scaleX: 1, scaleY: 1, rotation: 0, offsetLeft: 0, offsetTop: 0 }
  const crop = getCropFromScale(artW, artH, transform.scaleX ?? transform.scale, transform.offsetLeft, transform.offsetTop, imageAspect, 3, transform.scaleY ?? transform.scaleX ?? transform.scale, transform.rotation)
  updateArtworkGeometry(instance, artW, artH, crop, artworkTexture, transform)
  updateDependentShellGeometry(instance, metrics, {
    sourceCrop: crop,
    sourceTransform: transform,
    artworkTexture,
    substrateBackColor,
  })
}

export function applyDesignStudioItemSourceVisual(instance, {
  artWidth,
  artHeight,
  transform,
  imageAspect,
  artworkTexture = null,
  substrateBackColor = null,
  baseDims = null,
} = {}) {
  if (!instance?.refs || !transform) return
  const dims = baseDims || instance.builtDims || {}
  const artW = parseNumber(artWidth ?? dims.artW)
  const artH = parseNumber(artHeight ?? dims.artH)
  if (!(artW > 0) || !(artH > 0)) return
  const crop = getCropFromScale(artW, artH, transform.scaleX ?? transform.scale, transform.offsetLeft, transform.offsetTop, imageAspect, 3, transform.scaleY ?? transform.scaleX ?? transform.scale, transform.rotation)

  if (instance.refs.artworkMesh?.material?.map) {
    updateArtworkMeshTexture(instance.refs.artworkMesh, artworkTexture, artW, artH, crop, transform)
  } else if (instance.refs.canvasWrapMesh?.userData?.integratedArtwork) {
    updateCanvasWrapArtwork(instance.refs.canvasWrapMesh, {
      artworkTexture: artworkTexture || instance.refs.canvasWrapMesh.userData?.artworkTexture,
      artworkWidth: artW,
      artworkHeight: artH,
      fullWidth: crop.fullWidth,
      fullHeight: crop.fullHeight,
      cropLeft: crop.cropLeft,
      cropTop: crop.cropTop,
      scaleX: transform.scaleX ?? transform.scale,
      scaleY: transform.scaleY ?? transform.scaleX ?? transform.scale,
      rotation: transform.rotation,
    })
  } else if (instance.refs.substrateBackMesh?.userData?.integratedArtwork) {
    const metrics = resolveDesignStudioItemSceneMetrics({
      builtDims: dims,
      overrides: { artWidth: artW, artHeight: artH },
    })
    updateSubstrateBackArtwork(instance.refs.substrateBackMesh, metrics, {
      sourceCrop: crop,
      sourceTransform: transform,
      artworkTexture,
      substrateBackColor,
    })
  }
}

export function applyDesignStudioItemMatWidthVisual(instance, {
  matWidthsArray,
  sourceTransform = null,
  imageAspect = null,
  artworkTexture = null,
  substrateBackColor = null,
} = {}) {
  if (!instance?.refs || !Array.isArray(matWidthsArray) || !matWidthsArray.length) return
  const artW = parseNumber(instance.builtDims?.artW)
  const artH = parseNumber(instance.builtDims?.artH)
  if (!(artW > 0) || !(artH > 0)) return
  const metrics = resolveDesignStudioItemSceneMetrics({
    builtDims: instance.builtDims,
    overrides: { matWidthsArray },
  })
  const crop = sourceTransform
    ? getCropFromScale(artW, artH, sourceTransform.scaleX ?? sourceTransform.scale, sourceTransform.offsetLeft, sourceTransform.offsetTop, imageAspect, 3, sourceTransform.scaleY ?? sourceTransform.scaleX ?? sourceTransform.scale, sourceTransform.rotation)
    : null

  updateDependentShellGeometry(instance, metrics, {
    sourceCrop: crop,
    sourceTransform,
    artworkTexture,
    substrateBackColor,
    matWidthsArray,
  })
}

export async function updateDesignStudioItemObject(instance, renderSpec, options = {}) {
  if (!instance) return buildDesignStudioItemObject(renderSpec)
  const propsSignature = stableSignature(renderSpec?.rendererProps || {})
  const specArtW = parseNumber(renderSpec?.rendererProps?.artWidth)
  const specArtH = parseNumber(renderSpec?.rendererProps?.artHeight)
  const sizeMismatch = Number.isFinite(specArtW) &&
    Number.isFinite(specArtH) &&
    (Math.abs(parseNumber(instance.builtDims?.artW) - specArtW) >= 0.001 ||
      Math.abs(parseNumber(instance.builtDims?.artH) - specArtH) >= 0.001)
  if (instance.group?.userData?.propsSignature !== propsSignature || sizeMismatch) {
    const parent = instance.group?.parent || null
    const next = await buildDesignStudioItemObject(renderSpec)
    if (options.deferParentSwap) return next
    if (parent) {
      parent.add(next.group)
      parent.remove(instance.group)
    }
    disposeDesignStudioItemObject(instance)
    return next
  }
  applyTransform(instance.group, renderSpec || {})
  applyBackToWallAlignment(instance.group, instance.builtDims?.backDepth)
  if (instance.builtDims) {
    instance.builtDims.frontZ = instance.group.position.z + (instance.builtDims.localFrontZ || 0)
  }
  const selected = Boolean(renderSpec?.selected || renderSpec?.active)
  instance.group.userData.selected = selected
  instance.group.userData.active = selected
  instance.group.userData.affordances = renderSpec?.affordances || {}
  instance.group.traverse((obj) => {
    if (obj !== instance.group) {
      obj.userData = {
        ...obj.userData,
        selectedRenderItem: selected,
      }
    }
  })
  return instance
}

export function disposeDesignStudioItemObject(instance) {
  if (!instance) return
  if (instance.group?.parent) instance.group.parent.remove(instance.group)
  if (typeof instance.dispose === 'function') instance.dispose()
  else disposeObject3D(instance.group)
}
